import React, { useContext } from "react";
import { ContentContext } from "../App";

const About = () => {
  const { content } = useContext(ContentContext);

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)/,
      /youtube\.com\/live\/([^#&?]*)/,
      /youtube\.com\/shorts\/([^#&?]*)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[1].length >= 11) {
        return match[1].substring(0, 11);
      }
    }
    return null;
  };

  const aboutVideos = content.about_videos || [];

  return (
    <section id="sobre-nosotros" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-8">
            {content.about_title || "Sobre Vivendius"}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 px-4">
            {content.about_description || "Somos una empresa especializada en flipping house en Barcelona y alrededores. Compramos viviendas con potencial, las reformamos completamente con los mejores acabados y las ponemos a la venta como nuevas."}
          </p>
          
          {/* Videos Section */}
          {aboutVideos.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Así Trabajamos</h3>
              <div className={`grid gap-6 ${
                aboutVideos.length === 1 
                  ? 'grid-cols-1 max-w-3xl mx-auto' 
                  : aboutVideos.length === 2 
                    ? 'grid-cols-1 md:grid-cols-2' 
                    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {aboutVideos.map((video, index) => {
                  const videoId = getYouTubeId(video.youtube_url);
                  return (
                    <div key={video.id || index} className="bg-gray-50 rounded-lg overflow-hidden shadow-md">
                      {videoId ? (
                        <div className="aspect-video">
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={video.title || `Video ${index + 1}`}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gray-200 flex items-center justify-center">
                          <p className="text-gray-500">Video no disponible</p>
                        </div>
                      )}
                      {video.title && (
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-800">{video.title}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Compramos</h3>
              <p className="text-gray-600">Identificamos viviendas con potencial en las mejores ubicaciones</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Reformamos</h3>
              <p className="text-gray-600">Transformamos completamente cada vivienda con acabados de calidad</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Vendemos</h3>
              <p className="text-gray-600">Ponemos a la venta viviendas como nuevas listas para habitar</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;